/**
 * Cart-exit small-prints upsell (owner 2026-08-17). Fires ONCE, on the
 * Checkout tap, BEFORE checkout begins (never inside it — Baymard's 66%
 * frustration finding is about in-checkout cross-sells; this is our own cart
 * page). The offer is built by lib/upsell.smallPrintsOffer under the impulse
 * budget rule (≤25% of cart, capped $12): a handful of the customer's OWN
 * photos, each at a size that fits its ratio perfectly and clears goodDpi,
 * so accepting can never create a crop or low-res flag.
 *
 * One tap adds every offered print; either button proceeds to checkout.
 */
import { parseSizeId, type PaperId, type PricingData } from '@rpl/pricing';
import { useState } from 'react';
import { formatMoney } from '../../lib/format';
import { sizeShort } from '../../lib/sizePaper';
import type { SmallPrintsOffer } from '../../lib/upsell';
import type { LocalPhoto } from '../../lib/types';
import { ModalShell } from '../sizepaper/ModalShell';
import { Segmented } from '../sizepaper/Segmented';

export function SmallPrintsSheet({
  offer,
  photosById,
  onAdd,
  onSkip,
}: {
  pricing?: PricingData;
  offer: SmallPrintsOffer;
  photosById: Map<string, LocalPhoto>;
  /** Chosen C-Type finish rides along (gloss and lustre are price-identical). */
  onAdd: (paper: PaperId) => void;
  onSkip: () => void;
}) {
  // Owner 2026-08-17: quick finish toggle, LUSTRE default, never a dropdown.
  const [paper, setPaper] = useState<'gloss' | 'lustre'>('lustre');
  const n = offer.items.length;
  const thumbs = offer.items.slice(0, 6);
  /* Owner 2026-08-17: the sheet must say EXACTLY what the tap adds (size,
     one per photo, unit price) — after accepting, the turnaround dialog
     covers the cart, so this copy is the only receipt they see. */
  const bySize = new Map<string, { count: number; price: number }>();
  for (const it of offer.items) {
    const slot = bySize.get(it.sizeId) ?? { count: 0, price: it.price };
    slot.count += 1;
    bySize.set(it.sizeId, slot);
  }
  const short = (sizeId: string) => sizeShort(parseSizeId(sizeId).size);
  const terms =
    bySize.size === 1
      ? `One ${short(offer.items[0]!.sizeId)}″ ${paper} print of ${n === 1 ? 'this photo' : 'each photo'} · ${formatMoney(offer.items[0]!.price)} each`
      : [...bySize.entries()].map(([id, v]) => `${v.count} × ${short(id)}″`).join(' · ') +
        ' · one print per photo';
  const cta =
    n === 1
      ? `Add a ${short(offer.items[0]!.sizeId)}″ print · +${formatMoney(offer.total)}`
      : `Add ${n} prints · +${formatMoney(offer.total)}`;
  return (
    <ModalShell onClose={onSkip} width={420} label="Add small prints?" className="spsheet">
      <div className="spsheet__thumbs" aria-hidden>
        {thumbs.map((it) => {
          const ph = photosById.get(it.photoId);
          return ph?.previewUrl ? (
            <img key={it.photoId} className="spsheet__thumb" src={ph.previewUrl} alt="" />
          ) : (
            <span key={it.photoId} className="spsheet__thumb spsheet__thumb--ph" />
          );
        })}
        {n > thumbs.length && <span className="spsheet__more">+{n - thumbs.length}</span>}
      </div>
      <div className="spsheet__body">
        <div className="spsheet__title">Smaller prints to share?</div>
        <p className="spsheet__detail">
          Your big print goes on the wall. Add smaller copies to share around, sized to fit with
          nothing to crop.
        </p>
        <p className="spsheet__terms">{terms}</p>
        <div className="spsheet__row">
          <Segmented
            variant="light"
            ariaLabel="Finish"
            segments={[
              { key: 'lustre', label: 'Lustre' },
              { key: 'gloss', label: 'Gloss' },
            ]}
            value={paper}
            onChange={(key) => setPaper(key as 'gloss' | 'lustre')}
          />
          <button type="button" className="btn btn--gold spsheet__cta" onClick={() => onAdd(paper)}>
            {cta}
          </button>
        </div>
        <button type="button" className="spsheet__no" onClick={onSkip}>
          No thanks, checkout
        </button>
      </div>
    </ModalShell>
  );
}
